// @ts-strict-ignore
import clsx from 'clsx';
import omit from 'lodash/omit';
import camelCase from 'lodash/camelCase';
import {motion} from 'motion/react';
import React, {forwardRef} from 'react';
import produce from 'immer';

import {ButtonReact} from 'common/components/button';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {prompt} from 'common/components/confirm';
import {SvgIconReact} from 'common/components/svg-icon';
import {useTranslation} from 'common/i18n/hooks';
import {MetricUnitsButton} from 'common/query-builder/chart-customization/metric-units';
import {CallingLocation} from 'common/query-builder/chart-customization/tracking';
import {ControlBarAction, ControlBarActionItems} from 'iron/common/query-builder/query-entry-section/constants';
import {DataGroupSelect} from 'common/query-builder/data-group-select';
import {EntryRowProps} from 'common/query-builder/query-entry-section/row-types/types';
import {Action, MetricEntry} from 'common/query-builder/query-entry-section/types';
import {MetricType, TopLevelMetricType} from 'common/types/reports/bookmark';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {useMetricEntries} from 'common/report/insights/contexts/metrics';
import {assert} from 'common/util';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {DisplaySize} from 'common/components/tooltip/types';
import {NewTooltip} from 'common/components/tooltip/new-tooltip';
import {hasSaveableBehavior} from 'common/report/insights/contexts/utils/behaviors';
import behaviorSvg from 'common/assets/images/query/saved_behavior.svg';
import metricSvg from 'common/assets/images/query/saved_metric.svg';
import {BehaviorTypeIcons, MetricTypeIcons} from 'common/report/insights/util/metrics';

import styles from './index.module.styl';
// oxlint-disable-next-line import/no-cycle
import {ActiveMenuDetail, MetricParts, RowProps, UpdateEntryOpts} from './types/catalog';
import {OverflowControls} from './common/controls';
// oxlint-disable-next-line no-cycle
import {MetricSelector} from './common/metric-selector';
// oxlint-disable-next-line import/no-cycle
import {MetricBehaviorBase} from './types/base';
import {checkUpsellForMetric, getNewSimpleMetricEntry} from './utils';
import {ValidMetricTypes} from './types/constants';

type NewMetricEntryProps = RowProps & {
  isExpanded?: boolean;
  /** when provided, allows for a drag and drop gripper element */
  handleRef?: (element: Element | null) => void;
};

/**
 * MetricEntry renders every possible metric type Insights supports. Within this type of metric
 * you can have saved or unsaved behavior definitions (funnel, retention or simple behaviors).
 *
 * All entity management interactions like saving, renaming, etc... are handled in this component.
 *
 * @see https://www.notion.so/mxpnl/Saved-Formulas-Tech-Design-961190b1eaa34cfd9907d57d0d6b2f49?pvs=4
 */
export const NewMetricEntry = forwardRef<HTMLDivElement, NewMetricEntryProps>(function (props, ref) {
  const [isExpanded, setIsExpanded] = React.useState((props.sectionProps.readonly || props.isExpanded) ?? true);
  const [isSaving, setIsSaving] = React.useState(false);
  const {saveMetric, resetMetric, getSaveMetricDisabledMessage, saveBehavior, getClause, isValidClause} =
    useMetricEntries();
  const metrics = useMetrics();
  const {t} = useTranslation();
  const currentProject = useCurrentProject()?.get()?.value;
  const hasMetricLevelDataGroups = props.sectionProps.metricLevelDataGroups;
  const checkUpsell = checkUpsellForMetric(currentProject);

  // we use a ref instead of state since it can be the case different code paths in a metric update
  // the active menu. We need to most up to date active menu for orchestrating open dropdowns and not the current rendered value
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
  const activeMenu = React.useRef<ActiveMenuDetail>(null);

  // contains the actual bookmark clause for this metric entry
  const clause = getClause({index: props.index, parentMetricIndex: props.parentMetricIndex});
  const hasValidClause = isValidClause({index: props.index, parentMetricIndex: props.parentMetricIndex});

  const metric = props.entry;
  const behavior = metric.behavior;
  const isFormulaEntry = props.entry.type === TopLevelMetricType.NewFormulaEntry;
  const metricType = (isFormulaEntry ? MetricType.Formula : behavior.type) as ValidMetricTypes;
  const isPeopleBasedMetric = [MetricType.People, MetricType.Cohort].includes(metricType);

  assert(metricType in MetricParts, `attempted to render an unsupported metric type`);

  const MetricMeasurement = MetricParts[metricType].measurement;
  const MetricBehavior = MetricParts[metricType].definition;

  /** any change to a saved metric will result in the save button being enabled */
  const updateMetric = (updates: Partial<MetricEntry>, opts?: UpdateEntryOpts) => {
    props.updateEntry(
      {
        ...props.entry,
        ...updates,
      },
      opts,
    );
  };

  /** augmented props to pass on to child components */
  const childProps: RowProps = {
    ...omit(props, `ref`, `handleRef`),
    isValidClause: hasValidClause,
    updateEntry: updateMetric,
    activeMenu: activeMenu.current,
    setActiveMenu: (menu) => {
      activeMenu.current = menu;
      forceUpdate();
    },
  };

  /** computes any per-metric type custom actions, like customize buckets for simple metrics, or SCD property actions */
  const customMetricActions = MetricParts[metricType].customActions?.(childProps) ?? [];
  const hasUnsavedChanges = props.entry.id ? props.entry.hasUnsavedChanges : false;

  /** we only want to display the behavior block when working with saved behaviors */
  const canSaveBehavior = hasSaveableBehavior(clause);
  const shouldDisplayBehaviorControls = props.entry.behavior?.id && canSaveBehavior;

  /** @todo(metrics) this should a disableAlterEntries prop */
  const canAlterEntries = !props.sectionProps.readonly && !props.sectionProps.disableAddEntry;
  const canRemoveEntry = canAlterEntries && props.removeEntry;

  const updateDisabledMessage = getSaveMetricDisabledMessage({
    index: props.index,
    parentMetricIndex: props.parentMetricIndex,
  });
  const savingDisabledMessage = getSaveMetricDisabledMessage({
    index: props.index,
    parentMetricIndex: props.parentMetricIndex,
    omitPermissionsCheck: true,
  });

  const handleSaveMetric = async (saveAsNew = false) => {
    if (checkUpsell.shouldUpsell) {
      checkUpsell.upsellAction();
      return;
    }

    setIsSaving(true);
    await saveMetric({
      index: props.index,
      parentMetricIndex: props.parentMetricIndex,
      saveAsNew,
    });
    setIsSaving(false);
  };

  const handleSaveBehavior = async (saveAsNew = false) => {
    if (checkUpsell.shouldUpsell) {
      return checkUpsell.upsellAction();
    }

    setIsSaving(true);
    await saveBehavior({
      index: props.index,
      parentMetricIndex: props.parentMetricIndex,
      saveAsNew,
    });
    setIsSaving(false);
  };

  const handleCollapse = () => setIsExpanded(!isExpanded);

  const handleHideMetric = () => updateMetric({isHidden: !props.entry.isHidden});

  const handleRemoveMetric = () => props.removeEntry();

  const handleResetMetric = () => {
    resetMetric({
      index: props.index,
      parentMetricIndex: props.parentMetricIndex,
    });
  };

  /** detach from saved entity when cloning a metric */
  const handleDuplicateMetric = () =>
    props.cloneEntry({
      ...props.entry,
      _idx: undefined,
      id: null,
      name: metric.name && isFormulaEntry ? `${metric.name} (${t(`common.copy`)})` : undefined,
      userNamed: false,
      isExpanded: true,
      isHidden: false,
    });

  const handleRename = async () => {
    const promptResponse = await prompt(
      {
        /** @todo(metrics) plug in automatic naming here */
        'initial-input-value': metric.name || t(`query-builder.metrics.new-definition`),
        'allow-empty-input-value': false,
        'show-input': true,
      },
      {
        inputValidator(stagedInput) {
          const trimmed = stagedInput.trim();
          const labelIsUnchanged = trimmed === metric.name;
          const isUnique = metrics
            .getAllValues()
            .filter((value) => value.id !== props.entry.id)
            .every((value) => value.name !== trimmed);
          return labelIsUnchanged || isUnique ? null : t(`components.create-entity.errors.name-exists`);
        },
      },
      {
        title: ({t}) => t(`query-builder.actions.rename`),
        confirmButtonLabel: ({t}) => t(`common.done`),
      },
    );

    if (promptResponse.didConfirm) {
      const name = promptResponse.inputValue.trim();
      updateMetric({
        name,
        userNamed: Boolean(name),
      });
    }
  };

  return (
    <div
      elref='entryRow'
      className={clsx(styles.entry, styles[camelCase(`is-${metricType}-metric`)], {
        [styles.isHidden]: props.entry.isHidden,
      })}
    >
      {/* metric header area */}
      <div ref={canAlterEntries ? ref : null} className={styles.header}>
        {/* drag and rop gripper */}
        {canAlterEntries && props.handleRef && (
          <NewTooltip content='Drag to move' size={DisplaySize.Small} placement='top'>
            <div className={styles.gripper}>
              <SvgIconReact icon='gripper-xs' />
            </div>
          </NewTooltip>
        )}

        {/* icon/collapse button */}
        <ButtonReact
          className={clsx(styles.collapseButton, {
            [styles.hasUnsavedChanges]: hasUnsavedChanges,
          })}
          theme={ButtonTheme.Tertiary}
          onClick={handleCollapse}
          tooltip={
            hasUnsavedChanges
              ? {
                  text: t(`query-builder.metrics.unsaved-changes-tooltip`),
                  size: DisplaySize.Medium,
                }
              : null
          }
        >
          <span className={styles.label}>{props.rowLabel}</span>
          <SvgIconReact className={styles.caret} icon='caret-down' />
        </ButtonReact>

        <div className={clsx(styles.content, {[styles.isSaved]: Boolean(props.entry.id)})}>
          <MetricSelector {...childProps} />
        </div>

        {/* indicate sync status  */}
        {isSaving && <span className={styles.status}>{t(`common.saving_suspense`)}</span>}

        {/* metric controls */}
        <div className={clsx(styles.actions, {[styles.isOpen]: activeMenu.current?.menu === `metric-overflow`})}>
          {/* delete metric button */}
          {canRemoveEntry && <ButtonReact theme={ButtonTheme.Tertiary} icon='x' onClick={() => handleRemoveMetric()} />}

          {/* metric overflow controls for saving, resetting, etc... */}
          <OverflowControls
            onMenuChange={(isOpen) => {
              if (isOpen) {
                childProps.setActiveMenu({menu: `metric-overflow`});
              } else if (activeMenu?.current?.menu === `metric-overflow`) {
                childProps.setActiveMenu(null);
              }
            }}
            items={[
              {
                items: [
                  // show "Update Metric" only when saved and with local changes
                  metric.id &&
                    metric.hasUnsavedChanges && {
                      label: ({t}) => t(`query-builder.actions.update`),
                      icon: `push-up`,
                      onSelect: () => handleSaveMetric(),
                      isDisabled: Boolean(updateDisabledMessage),
                      tooltip: updateDisabledMessage ? {text: updateDisabledMessage} : null,
                    },

                  // show "Save Metric" only when unsaved
                  // Don't show for Retention Frequency metrics
                  !metric.id &&
                    metricType !== MetricType.RetentionFrequency && {
                      label: ({t}) => t(`query-builder.actions.save-metric`),
                      icon: MetricTypeIcons[metricType],
                      onSelect: () => handleSaveMetric(),
                      ...(checkUpsell.shouldUpsell && {
                        icon: `brand-upgrade`,
                      }),
                      isDisabled: Boolean(savingDisabledMessage),
                      tooltip:
                        savingDisabledMessage && typeof savingDisabledMessage === `string`
                          ? {text: savingDisabledMessage}
                          : {
                              'image-src': metricSvg,
                              text: ({t}) => t(`query-builder.actions.save-metric-tooltip`),
                              size: DisplaySize.Small,
                            },
                    },

                  // show "Save Behavior" only when working with metrics with unsaved behaviors
                  canSaveBehavior &&
                    props.entry.behavior &&
                    !props.entry.behavior.id && {
                      label: `Save Behavior`,
                      icon: BehaviorTypeIcons[props.entry.behavior.type],
                      onSelect: () => handleSaveBehavior(),
                      ...(checkUpsell.shouldUpsell && {icon: `brand-upgrade`}),
                      isDisabled: Boolean(savingDisabledMessage),
                      tooltip:
                        savingDisabledMessage && typeof savingDisabledMessage === `string`
                          ? {text: savingDisabledMessage}
                          : {
                              'image-src': behaviorSvg,
                              text: ({t}) => t(`query-builder.actions.save-behavior-tooltip`),
                              size: DisplaySize.Small,
                            },
                    },

                  // show "Save as New Metric" only when rendering a saved metric
                  metric.id && {
                    label: ({t}) => t(`common.save-as-new`),
                    icon: `save`,
                    onSelect: () => handleSaveMetric(true),
                    ...(checkUpsell.shouldUpsell && {
                      icon: `brand-upgrade`,
                    }),
                    isDisabled: Boolean(savingDisabledMessage),
                    tooltip: savingDisabledMessage ? {text: savingDisabledMessage} : null,
                  },

                  // show "Reset" only when dealing with a saved metric with local changes
                  metric.id &&
                    metric.hasUnsavedChanges && {
                      label: ({t}) => t(`query-builder.actions.reset`),
                      icon: `undo`,
                      onSelect: handleResetMetric,
                    },

                  // rename metric
                  {...ControlBarActionItems[ControlBarAction.Rename], onSelect: handleRename},

                  // any metric custom actions
                  ...customMetricActions,

                  // hide metric
                  canAlterEntries &&
                    !props.disableHideEntry && {
                      ...ControlBarActionItems[props.entry.isHidden ? ControlBarAction.Show : ControlBarAction.Hide],
                      onSelect: handleHideMetric,
                    },
                  // clone metric
                  canAlterEntries && {
                    ...ControlBarActionItems[ControlBarAction.Duplicate],
                    isDisabled: props.sectionProps.disableCloneEntry,
                    tooltip: props.sectionProps.disableCloneEntry && {
                      text: ({t}) => t(`query-builder.actions.duplicate-disabled-tooltip`),
                      size: DisplaySize.Small,
                    },
                    onSelect: handleDuplicateMetric,
                  },

                  // remove metric
                  canRemoveEntry && {
                    ...ControlBarActionItems[ControlBarAction.Delete],
                    onSelect: handleRemoveMetric,
                  },
                ].filter(Boolean),
              },
            ]}
          />
        </div>
      </div>

      {/* metric body */}
      <motion.div
        className={styles.body}
        transition={{
          type: `spring`,
          visualDuration: 0.1,
          bounce: 0.3,
        }}
        initial={isExpanded ? `expanded` : `collapsed`}
        animate={isExpanded ? `expanded` : `collapsed`}
        variants={{
          expanded: {height: `auto`, overflow: `visible`},
          collapsed: {height: 0, overflow: `hidden`},
        }}
      >
        <div className={styles.content}>
          {/* formula metrics do not render a behavior block */}
          {shouldDisplayBehaviorControls ? (
            <MetricBehaviorBase
              {...childProps}
              behavior={props.entry.behavior}
              metricType={metricType}
              updateEntry={updateMetric}
            >
              <MetricBehavior {...childProps} />
            </MetricBehaviorBase>
          ) : (
            <MetricBehavior {...childProps} />
          )}
        </div>

        {/* metric measurement options */}
        <div className={styles.measurement}>
          <MetricMeasurement {...childProps}>
            {/* metric units to display a result like a percent, currency or number */}
            <MetricUnitsButton
              callingLocation={CallingLocation.Metric}
              chartType={props.currentChartType}
              entry={props.entry}
              size={ButtonSize.Small}
              setEntry={(entry) => props.updateEntry(entry as MetricEntry, {requery: false})}
            />

            <div className={styles.options}>
              {/* data group selector */}
              {hasMetricLevelDataGroups && props.entry.type !== TopLevelMetricType.NewFormulaEntry && (
                <DataGroupSelect
                  selectedDataGroup={isPeopleBasedMetric ? behavior.dataGroupId : metric.measurement.dataGroupId}
                  onSelect={(id) => {
                    const updatedEntry = produce(props.entry, (draft) => {
                      if (isPeopleBasedMetric) {
                        draft.behavior.dataGroupId = id;
                      } else {
                        draft.measurement.dataGroupId = id;
                      }
                    });
                    props.updateEntry(updatedEntry);
                  }}
                  {...(metricType === MetricType.Cohort
                    ? {readonly: true, disabledReason: t(`data-groups.analyze-by-cohort-group`)}
                    : {})}
                />
              )}
            </div>
          </MetricMeasurement>
        </div>
      </motion.div>
    </div>
  );
});

const AddMetricEntry = function (props: EntryRowProps<MetricEntry>) {
  const {t} = useTranslation();
  return (
    <div className={styles.entry}>
      <div className={styles.header}>
        {/* icon/collapse button */}
        <ButtonReact className={styles.collapseButton} theme={ButtonTheme.Tertiary}>
          <span className={styles.label}>{props.getEntryLabel(props.index + 1)}</span>
        </ButtonReact>
        <MetricSelector
          {...props}
          disableContextPanel
          index={props.index + 1}
          updateEntry={() => null}
          cloneEntry={() => null}
          removeEntry={() => null}
          rowLabel={props.getEntryLabel(props.index + 1)}
          entry={{
            name: t(`query-builder.add-metric`),
            ...getNewSimpleMetricEntry({isExpanded: false}),
          }}
        />
      </div>
    </div>
  );
};

/** For QueryEntrySection */
export const NewMetricEntryRow = forwardRef<HTMLDivElement, EntryRowProps<MetricEntry>>(function (props, ref) {
  return (
    <>
      <NewMetricEntry
        {...props}
        ref={ref}
        updateEntry={(entry, opts) => {
          props.updateEntry(Action.Edit, entry, opts);
        }}
        cloneEntry={(entry) => {
          props.updateEntry(Action.Clone, entry);
        }}
        removeEntry={() => props.removeEntry()}
      />
      {!props.disableAddEntry &&
      !props.sectionProps.disableAddEntryReason &&
      props.getEntries().length - 1 === props.index &&
      !props.hasStagedRow ? (
        <AddMetricEntry {...props} />
      ) : null}
    </>
  );
});
